// Pantalla de login — el MISMO diseño que PILOT (web/src/Login.jsx, rediseño
// 2026-08): pantalla dividida con el panel de marca (gradiente marino + blobs
// que derivan + la raya roja del logo) a la izquierda, y el formulario
// respirando a la derecha. En móvil el panel se esconde y el logo encabeza el
// formulario.
//
// Lo importante al entrar: se deja la bandera "radar_entrada" en
// sessionStorage ANTES del signIn (si falla, se quita). App enciende el TELÓN
// con la palomita al detectar la sesión. La bandera va antes del await a
// propósito: onAuthStateChange puede disparar el cambio de pantalla antes de
// que nuestro await regrese.
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type MouseEvent,
} from "react";
import { supa } from "./lib/supabase";
import { tomarAvisoSesionVencida } from "./lib/rpc";
import { publico } from "./lib/rutas";
import { cuentaRecordada, olvidarCuentaRecordada } from "./lib/cuentaRecordada";
import { BANDERA_ENTRADA } from "./components/TelonEntrada";
import BotonTema from "./components/BotonTema";
import { Icono } from "./components/Base";

const turno = (i: number) => ({ "--i": i }) as CSSProperties;

export default function Login({ mensaje }: { mensaje?: string | null }) {
  // Si ya se entró antes en este navegador, el login saluda por nombre y solo
  // pide la contraseña (igual que la app actual, app.js prepararLogin).
  const [cuenta, setCuenta] = useState(cuentaRecordada);
  const [email, setEmail] = useState(cuenta?.email ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(mensaje ?? null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [verPass, setVerPass] = useState(false);
  // la foto guardada puede haberse borrado del Storage: si no carga, se cae al
  // icono genérico en vez de dejar la imagen rota
  const [fotoRota, setFotoRota] = useState(false);

  // Si llegamos aquí porque el token venció a media consulta, decirlo: sin
  // esto el login aparece mudo y parece que la app se cerró sola. La nota se
  // lee en un efecto (no al crear el estado) para no consumirla dos veces con
  // el doble render de StrictMode. `mensaje` manda: es más específico.
  useEffect(() => {
    const nota = tomarAvisoSesionVencida();
    if (nota) setError((prev) => prev ?? nota);
  }, []);

  const refEmail = useRef<HTMLInputElement>(null);
  const [pedirFoco, setPedirFoco] = useState(false);
  // El foco va en un efecto y no en el clic: al soltar la cuenta recordada, el
  // campo de correo todavía está oculto en ese instante y no es enfocable.
  useEffect(() => {
    if (!pedirFoco) return;
    setPedirFoco(false);
    refEmail.current?.focus();
  }, [pedirFoco]);

  function otraCuenta(e: MouseEvent) {
    e.preventDefault();
    olvidarCuentaRecordada();
    setCuenta(null);
    setEmail("");
    setPassword("");
    setError(null);
    setAviso(null);
    setPedirFoco(true);
  }

  async function entrar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true);
    setError(null);
    sessionStorage.setItem(BANDERA_ENTRADA, "1");
    const { error } = await supa.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    if (error) {
      sessionStorage.removeItem(BANDERA_ENTRADA);
      setEnviando(false);
      // Mensajes honestos, como PILOT: decir "contraseña incorrecta" cuando el
      // problema es la red manda a la persona a cambiarla sin necesidad.
      // Supabase marca credenciales malas con 400/422; el 429 es bloqueo por
      // demasiados intentos y ahí "reintenta" solo alarga la espera.
      const credenciales = error.status === 400 || error.status === 422;
      const bloqueado = error.status === 429;
      setError(
        bloqueado
          ? "Demasiados intentos seguidos. Espera unos minutos antes de volver a intentar — reintentar ahora solo alarga la espera."
          : credenciales
            ? "Correo o contraseña incorrectos."
            : "No se pudo conectar para validar tu acceso. Revisa tu internet y vuelve a intentar." +
              (error.message ? " (" + error.message + ")" : ""),
      );
    }
    // Si entró bien NO hacemos nada más (ni setEnviando(false)): el spinner
    // sigue mientras App detecta la sesión y pinta el telón.
  }

  async function olvide(e: MouseEvent) {
    e.preventDefault();
    if (enviando) return;
    setError(null);
    setAviso(null);
    if (!email.trim()) {
      setError("Escribe tu correo arriba y vuelve a dar clic.");
      return;
    }
    setEnviando(true);
    // El enlace tiene que caer donde EXISTE la pantalla de crear contraseña:
    // aquí mismo. BASE_URL y no "/beta/" a mano para que siga apuntando bien
    // el día que la beta pase a ser la app de raíz.
    const { error } = await supa.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: window.location.origin + import.meta.env.BASE_URL,
    });
    setEnviando(false);
    if (error) {
      setError("No se pudo enviar el correo. Intenta de nuevo.");
      return;
    }
    setAviso("Te enviamos un correo con el enlace para restablecerla.");
  }

  return (
    <div className="login-split">
      <div className="login-marca" aria-hidden="true">
        <div className="blob b1"></div>
        <div className="blob b2"></div>
        <div className="blob b3"></div>
        <div className="marca-contenido">
          <div className="marca-kex">KEX</div>
          <div className="marca-radar-grande">R A D A R</div>
          <div className="marca-raya"></div>
          <p className="marca-frase">
            El radar de Kitchen Experts. Leads, ventas, marketing y servicio,
            en un solo lugar.
          </p>
        </div>
      </div>

      <div className="login-lado">
        <div className="login-tema">
          <BotonTema />
        </div>
        <form className="login-caja" onSubmit={entrar}>
          {/* El logo con la marca de la app al lado. Va aquí y no solo en el
              panel lateral porque ese panel se esconde en celular, y es justo
              ahí donde más falta saber si estás en Radar o en PILOT. */}
          <div className="login-marca-app entra" style={turno(0)}>
            <img
              className="login-logo"
              src={publico("logo-kex.png")}
              alt="Kitchen Experts"
            />
            <span className="app-chip">Radar</span>
          </div>
          <h1 className="entra" style={turno(0)}>Hola de nuevo</h1>
          <p className="login-sub entra" style={turno(1)}>
            {cuenta
              ? "Escribe tu contraseña para continuar."
              : "Entra con tu cuenta de KEX."}
          </p>

          {cuenta && (
            <div className="cuenta-prev entra" style={turno(2)}>
              {cuenta.avatar && !fotoRota ? (
                <img
                  className="cuenta-prev-foto"
                  src={cuenta.avatar}
                  alt=""
                  onError={() => setFotoRota(true)}
                />
              ) : (
                <span className="cuenta-prev-icono">
                  <Icono id="i-user" clase="ic ic-lg" />
                </span>
              )}
              <span className="cuenta-prev-txt">
                <b>{cuenta.nombre || cuenta.email}</b>
                {cuenta.nombre && <span>{cuenta.email}</span>}
              </span>
            </div>
          )}

          {/* Con cuenta recordada el campo sigue en el formulario pero oculto:
              es el que le dice al gestor de contraseñas de quién es la
              contraseña que está autocompletando. Su valor nunca queda vacío
              (viene de la cuenta), así que `required` no bloquea el envío. */}
          <div className={"campo entra" + (cuenta ? " oculto" : "")} style={turno(2)}>
            <label htmlFor="email">Correo</label>
            <input
              ref={refEmail}
              id="email"
              type="email"
              autoComplete="username"
              placeholder="tu@kex.com.mx"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="campo entra" style={turno(3)}>
            <label htmlFor="password">Contraseña</label>
            <div className="campo-control">
              <input
                id="password"
                type={verPass ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="ver-pass"
                onClick={() => setVerPass((v) => !v)}
                aria-label={verPass ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="3" />
                  {verPass && <path d="M4 4l16 16" />}
                </svg>
              </button>
            </div>
          </div>

          <button className="btn btn-entrar entra" style={turno(4)} type="submit" disabled={enviando}>
            {enviando ? <span className="spinner" aria-label="Entrando" /> : "Entrar"}
          </button>

          {error && <div className="error">{error}</div>}

          <p className="entra" style={{ ...turno(5), margin: "4px 0 0" }}>
            <a href="#" className="login-olvide" onClick={olvide}>
              ¿Olvidaste tu contraseña?
            </a>
          </p>

          {cuenta && (
            <p className="entra" style={{ ...turno(6), margin: "8px 0 0" }}>
              <a href="#" className="login-otra-cuenta" onClick={otraCuenta}>
                Entrar con otra cuenta
              </a>
            </p>
          )}

          {aviso && (
            <div className="error" style={{ color: "var(--good-text, #079455)" }}>
              {aviso}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
